import { Box, Button, FormControlLabel, Tooltip, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Delete3Icon, EditIcon, PlusIcon } from "../assets/icons";
import { MainSpinner } from "../components";
import { Toggle } from "../components/Switch";
import Api from "../openapi";
import {
  AdminPlanDetailResponse,
  PlanDetailPlanOptionResponse,
  PlanDetailPlanOptionResponseMealTypeListEnum,
} from "../openapi/client";
import { setNotification } from "../store/notificationSlice";
import { generateRandom } from "../utils";
import useConfirm from "../hooks/useConfirm";
import { DeleteLabelIcon, PortionLabelIcon, SendLabelIcon } from "../components/IconLabel";
import theme from "../theme";
import PlanOptionCrud from "../components/PlanOptionCrud";
import useMealTypes from "../hooks/useMealTypes";
import PlanCrud from "../components/PlanCrud";
import { IEditPlan } from "./Plan";
import DataTable from "../components/DataTable";
import { GridRenderCellParams } from "@mui/x-data-grid";
import Can from "../components/Can";

interface IEditPlanOption {
  show: boolean;
  planOption?: PlanDetailPlanOptionResponse;
}

export default function PlanDetail() {
  const mealTypeList = useMealTypes();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [planReference, setPlan] = useState<AdminPlanDetailResponse>();
  const [plan, setPlanEdit] = useState<any>();
  const [createShow, setCreateShow] = useState(false);
  const [editShow, setEditShow] = useState<IEditPlanOption>({ show: false, planOption: undefined });
  const [editPlanShow, setEditPlanShow] = useState<IEditPlan>({ show: false, plan: undefined });
  const [loading, setLoading] = useState(false);
  const [formDirty, setFormDirty] = useState(false);
  const [retriggerReq, setRetriggerReq] = useState<number>();
  const [planStatusActive, setPlanStatusActive] = useState<boolean>(false);
  const [planStatusVisible, setPlanStatusVisible] = useState<boolean>(false);

  const [ConfirmDialog, confirmDelete] = useConfirm({
    titleIcon: <DeleteLabelIcon />,
    text: "You are about to permanently delete this plan, and this action is irreversible. Are you sure you want to proceed?",
  });

  const [ConfirmDialogPlanOption, confirmDeletePlanOption] = useConfirm({
    titleIcon: <DeleteLabelIcon />,
    text: "You are about to permanently delete this plan option, and this action is irreversible. Are you sure you want to proceed?",
  });

  const [ConfirmDialogPlanStatus, confirmDialogPlanStatus] = useConfirm({
    titleIcon: <SendLabelIcon />,
    text: "You are about to change the status of the plan. Are you sure you want to proceed?",
  });

  const [ConfirmDialogPlanVisible, confirmDialogPlanVisible] = useConfirm({
    titleIcon: <SendLabelIcon />,
    text: "You are about to change the visibility of the plan. Are you sure you want to proceed?",
  });

  const back = () => navigate(-1);

  const changeStatusPlan = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Boolean(e.target.checked);

    const answer = await confirmDialogPlanStatus();
    if (!answer) return;

    setPlanStatusActive(value);

    try {
      const { option, ...rest } = plan;
      const res = await Api.Plan.updatePlan(String(id), {
        ...rest,
        active: value,
      });
      setPlanEdit((state: any) => ({ ...state, active: value }));
    } catch (error: any) {
      setPlanStatusActive(!value);
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    }
  };

  const changeVisiblePlan = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Boolean(e.target.checked);

    const answer = await confirmDialogPlanVisible();
    if (!answer) return;

    setPlanStatusVisible(value);

    try {
      const { option, ...rest } = plan;
      const res = await Api.Plan.updatePlan(String(id), {
        ...rest,
        visible: value,
      });
      setPlanEdit((state: any) => ({ ...state, visible: value }));
    } catch (error: any) {
      setPlanStatusVisible(!value);
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    }
  };

  const create = () => {
    setCreateShow(true);
  };

  const edit = (params: any) => {
    setEditShow({ show: true, planOption: params.row });
  };
  const deletePlanOption = async (params: any) => {
    const answer = await confirmDeletePlanOption();
    if (!answer) return;

    try {
      const req = await Api.PlanOption.deletePlanOption(params.row.id);
      dispatch(
        setNotification({
          show: true,
          severity: "success",
          message: "Plan option has been deleted successfully",
        })
      );
      setRetriggerReq(generateRandom());
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    }
  };

  // plan edit and delete
  const editPlan = () => {
    setEditPlanShow({ show: true, plan });
  };

  const deletePlan = async () => {
    const answer = await confirmDelete();
    if (!answer) return;

    try {
      const req = await Api.Plan.deletePlan(String(plan.id));
      dispatch(
        setNotification({
          show: true,
          severity: "success",
          message: "Plan has been deleted successfully",
        })
      );
      navigate("/plan");
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    }
  };

  const handleClose = () => {
    setCreateShow(false);
    setEditShow({ show: false, planOption: undefined });
  };
  const handlePlanClose = () => {
    setCreateShow(false);
    setEditPlanShow({ show: false, plan: undefined });
  };

  const handleConfirm = () => {
    setRetriggerReq(generateRandom());
  };

  const getStatusLabel = (active: boolean) => {
    const statusText = active ? "Active" : "Inactive";
    const statusClass = active ? "active" : "passive";
    return <Box className={`status-label ${statusClass}`}>{statusText}</Box>;
  };

  const getVisibleLabel = (visible: boolean) => {
    const visibleText = visible ? "Visible" : "Invisible";
    const visibleClass = visible ? "active" : "passive";
    return <Box className={`status-label ${visibleClass}`}>{visibleText}</Box>;
  };

  const columns = useMemo(() => {
    return [
      {
        field: "name",
        minWidth: 150,
        flex: 1,
        headerName: "Name",
        sortable: true,
        hideable: false,
        valueGetter: (params: any) => params.row.name?.en || "",
      },
      {
        field: "selection",
        minWidth: 150,
        flex: 1,
        headerName: "Title",
        sortable: true,
        hideable: false,
        valueGetter: (params: any) => params.row.selection?.en || "",
      },
      {
        field: "mealTypeList",
        minWidth: 180,
        flex: 1.5,
        headerName: "Meal Types",
        sortable: true,
        hideable: false,
        valueGetter: (params: any) => {
          if (mealTypeList.length > 0) {
            return Array.from(params.row.mealTypeList as Set<PlanDetailPlanOptionResponseMealTypeListEnum>)
              ?.map((i) => mealTypeList.find((li) => li.name === i)?.description)
              .join(", ");
          }
          return "";
        },
      },
      {
        field: "period",
        minWidth: 120,
        flex: 0.8,
        headerName: "Delivery Days",
        sortable: true,
        hideable: false,
        valueGetter: (params: any) => `${params.row.period}`,
      },
      {
        field: "dailyPrice",
        minWidth: 100,
        flex: 0.8,
        headerName: "Daily Price",
        sortable: true,
        hideable: false,
        renderCell: (params: GridRenderCellParams<any, any>) => (
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 700,
              lineHeight: "normal",
              color: "primary.main",
            }}
          >
            {params.row.dailyPrice.toFixed(2)}
          </Typography>
        ),
      },
      {
        field: "active",
        minWidth: 100,
        flex: 0.8,
        headerName: "Status",
        sortable: true,
        hideable: false,
        renderCell: (params: GridRenderCellParams<any, any>) => getStatusLabel(params.row.active),
      },
      {
        field: "visible",
        minWidth: 100,
        flex: 0.8,
        headerName: "Visible",
        sortable: true,
        hideable: false,
        renderCell: (params: GridRenderCellParams<any, any>) => getVisibleLabel(params.row.visible),
      },
    ];
  }, [mealTypeList, planReference?.options]);

  useEffect(() => {
    const getPlan = async () => {
      try {
        setLoading(true);
        const res = await Api.Plan.getPlanById(String(id));
        setPlan(res.data);
        setPlanEdit(res.data);
        setPlanStatusActive(Boolean(res.data.active));
        setPlanStatusVisible(Boolean(res.data.visible));
      } catch (error) {}
      setLoading(false);
    };
    getPlan();
  }, [id, retriggerReq]);

  return (
    <Box className="page-wrapper">
      <Box className="section-breadcrumb">
        <Typography component={"span"}>{planReference?.name?.en}</Typography>
        <Can permission="PLAN_CREATE">
          <Button
            onClick={create}
            color="primary"
            size="small"
            variant="contained"
            sx={{ display: "flex" }}
            startIcon={<PlusIcon />}
          >
            Create New Plan Option
          </Button>
        </Can>
      </Box>
      <Box p={6} className="page-container" sx={{ display: "flex", flexDirection: "column", rowGap: "20px" }}>
        {loading && <MainSpinner />}
        <Box
          sx={{
            padding: "16px 24px",
            borderRadius: "8px",
            border: "1px solid #dde0e0",
            display: "flex",
            flexDirection: "column",
            rowGap: "8px",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontSize: "18px", fontWeight: 700, lineHeight: "normal", color: "text.dark" }}>
              {planReference?.name?.en}
            </Typography>
            <Box sx={{ display: "flex", gap: "24px" }}>
              <FormControlLabel
                sx={{
                  margin: 0,
                  columnGap: "8px",
                  "& .MuiFormControlLabel-label": { fontSize: "14px", fontWeight: 500 },
                }}
                control={<Toggle checked={planStatusActive} onChange={changeStatusPlan} />}
                label="Active"
              />
              <FormControlLabel
                sx={{
                  margin: 0,
                  columnGap: "8px",
                  "& .MuiFormControlLabel-label": { fontSize: "14px", fontWeight: 500 },
                }}
                control={<Toggle checked={planStatusVisible} onChange={changeVisiblePlan} />}
                label="Visible"
              />
            </Box>
          </Box>
          <Typography sx={{ fontSize: "12px", color: "text.fontLight3", fontWeight: 500, lineHeight: "160%" }}>
            {planReference?.description?.en}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", columnGap: "2px", flexWrap: "wrap" }}>
            <Typography sx={{ color: "text.dark", fontSize: "12px", fontWeight: 700, lineHeight: "normal" }}>
              Available Days:
            </Typography>
            {planReference?.availableDays?.map((i) => (
              <Typography
                sx={{
                  color: "text.dark2",
                  fontSize: "12px",
                  lineHeight: "normal",
                  textTransform: "capitalize",
                  "&:not(:last-of-type):after": {
                    content: "','",
                  },
                }}
              >
                {i.toLowerCase()}
              </Typography>
            ))}
          </Box>
          <Box mt={1} sx={{ display: "flex", columnGap: "8px", alignItems: "center" }}>
            <Can permission="PLAN_EDIT">
              <Button
                onClick={editPlan}
                startIcon={<EditIcon />}
                sx={{
                  background: "rgba(19, 111, 215, 0.08)",
                  fontSize: "12px",
                  fontWeight: 500,
                  lineHeight: "normal",
                  display: "flex",
                  padding: "8px 12px",
                  borderRadius: "4px",
                  "&": { color: "text.blue" },
                  "&:hover": { background: "rgba(19, 111, 215, 0.2)" },
                }}
              >
                Edit
              </Button>
            </Can>

            <Can permission="PLAN_DELETE">
              <Button
                onClick={deletePlan}
                startIcon={<Delete3Icon />}
                sx={{
                  background: "rgba(226, 64, 64, 0.08)",
                  fontSize: "12px",
                  fontWeight: 500,
                  lineHeight: "normal",
                  display: "flex",
                  padding: "8px 12px",
                  borderRadius: "4px",
                  "&": { color: "text.style" },
                  "&:hover": { background: "rgba(226, 64, 64, 0.2)" },
                }}
              >
                Delete
              </Button>
            </Can>
          </Box>
        </Box>
        <DataTable
          rows={planReference?.options || []}
          columns={columns}
          loading={loading}
          paginationMode="client"
          edit={edit}
          deleteRec={deletePlanOption}
          options={{
            hideToolbar: true,
            entityName: "plan option",
            permission: { edit: "PLAN_EDIT", delete: "PLAN_DELETE" },
          }}
        />
      </Box>
      <ConfirmDialog />
      <ConfirmDialogPlanOption />
      <ConfirmDialogPlanStatus />
      <ConfirmDialogPlanVisible />
      <PlanCrud
        edit
        open={editPlanShow.show}
        plan={editPlanShow.plan}
        title="Edit Plan"
        onClose={handlePlanClose}
        onConfirm={handleConfirm}
      />
      <PlanOptionCrud
        create
        open={createShow}
        planId={planReference?.id}
        planPeriod={planReference?.minDeliveryDaysOfWeek}
        title="Create New Plan Option"
        onClose={handleClose}
        onConfirm={handleConfirm}
      />
      <PlanOptionCrud
        edit
        open={editShow.show}
        planOption={editShow.planOption}
        planId={planReference?.id}
        planPeriod={planReference?.minDeliveryDaysOfWeek}
        title="Edit Plan Option"
        onClose={handleClose}
        onConfirm={handleConfirm}
      />
    </Box>
  );
}
